import { t } from "i18next"
import type Phaser from "phaser"
import { GameObjects } from "phaser"
import {
  OUTLAW_GOLD_REWARD,
  SHARDS_PER_SHINY_UNOWN_WANDERER,
  SHARDS_PER_UNOWN_WANDERER
} from "../../../../config"
import type { Wanderer } from "../../../../models/colyseus-models/wanderer"
import PokemonFactory from "../../../../models/pokemon-factory"
import { type Item, Transfer } from "../../../../types"
import { Orientation, PokemonActionState } from "../../../../types/enum/Game"
import { Pkm } from "../../../../types/enum/Pokemon"
import { WandererBehavior, WandererType } from "../../../../types/enum/Wanderer"
import { getFreeSpaceOnBench } from "../../../../utils/board"
import { clamp } from "../../../../utils/number"
import { chance } from "../../../../utils/random"
import { DEPTH } from "../depths"
import { isReplayRoom } from "../replay-room-id"
import type GameScene from "../scenes/game-scene"
import PokemonSprite from "./pokemon"
import PokemonSpecial from "./pokemon-special"

const DEFAULT_WANDERER_SPEED = 0.25

/*
List of wanderers:
- Unowns: give shard when caught
- Others: from Gotta catch em all scribble, added to bench when caught
*/

export default class WanderersManager {
  scene: GameScene

  constructor(scene: GameScene) {
    this.scene = scene
    scene.board?.player.wanderers.forEach((wanderer) => {
      this.addWanderer(wanderer)
    })
  }

  addWanderer(wanderer: Wanderer) {
    if (wanderer.type === WandererType.UNOWN) {
      this.addWanderingUnown(wanderer)
    } else if (wanderer.type === WandererType.UNOWN_SPELL) {
      this.addSpectatingUnown(wanderer)
    } else if (wanderer.type === WandererType.CATCHABLE) {
      this.addCatchableWanderer(wanderer)
    } else if (wanderer.type === WandererType.DIALOG) {
      this.addDialogWanderer(wanderer)
    } else if (wanderer.type === WandererType.OUTLAW) {
      this.addOutlawWanderer(wanderer)
    }
  }

  addWanderingUnown(wanderer: Wanderer) {
    this.addWandererPokemonSprite({
      wanderer,
      onClick: (wanderer, unownSprite, pointer) => {
        this.scene.room?.send(Transfer.WANDERER_CLICKED, { id: wanderer.id })
        this.displayShardGain(
          [pointer.x, pointer.y],
          unownSprite.pokemon.index,
          unownSprite.pokemon.shiny
        )
        unownSprite.destroy()
        return true
      }
    })
  }

  addSpectatingUnown(wanderer: Wanderer) {
    this.addWandererPokemonSprite({ wanderer })
  }

  addCatchableWanderer(wanderer: Wanderer) {
    this.addWandererPokemonSprite({
      wanderer,
      onClick: (wanderer, sprite, pointer) => {
        let caught = false
        if (this.scene.board) {
          if (getFreeSpaceOnBench(this.scene.board.player.board) > 0) {
            caught = true
            this.scene.room?.send(Transfer.WANDERER_CLICKED, {
              id: wanderer.id
            })
            sprite.destroy()
          } else {
            this.scene.board.displayText(pointer.x, pointer.y, t("full"), true)
          }
        }
        return caught
      }
    })
  }

  addOutlawWanderer(wanderer: Wanderer) {
    this.addWandererPokemonSprite({
      wanderer,
      speed: 0.4,
      onClick: (wanderer, sprite, pointer) => {
        let caught = false
        if (this.scene.board) {
          caught = true
          this.scene.room?.send(Transfer.WANDERER_CLICKED, {
            id: wanderer.id
          })
          sprite.destroy()
          this.scene.board.displayText(
            pointer.x,
            pointer.y - 40,
            t("caught"),
            true
          )
          this.scene.displayMoneyGain(pointer.x, pointer.y, OUTLAW_GOLD_REWARD)
        }
        return caught
      }
    })
  }

  addDialogWanderer(wanderer: Wanderer) {
    const sprite = new PokemonSpecial({
      scene: this.scene,
      x: -100,
      y: 350,
      name: wanderer.pkm,
      animation: PokemonActionState.WALK,
      ...getDialogsBySpecialWanderer(wanderer)
    })
    this.addWandererPokemonSprite({
      wanderer,
      existingSprite: sprite,
      onClick: (wanderer, sprite) => {
        //this.scene.room?.send(Transfer.WANDERER_CLICKED, { id: wanderer.id }) // not needed for dialog wanderers for now
        if (sprite.detail) {
          sprite.closeDetail()
        } else {
          sprite.openDetail()
          // scene-clocked so a still-pending fuse dies with the scene (a replay seek restarts it)
          this.scene.time.delayedCall(3000, () => {
            sprite.closeDetail()
          })
        }
        return false
      }
    })

    if (wanderer.pkm === Pkm.XATU && wanderer.data && this.scene.board) {
      const { chest, chestGroup } = this.scene.board.addChest(590, 450)
      this.scene.time.delayedCall(5000, () => {
        this.scene.board?.openChest(
          chestGroup,
          chest,
          wanderer.data.split(";") as Item[]
        )
      })
      this.scene.time.delayedCall(8000, () => {
        chestGroup.destroy(true, true)
      })
    }

    if (
      wanderer.pkm === Pkm.KECLEON_PURPLE &&
      wanderer.data &&
      this.scene.board
    ) {
      const group = this.scene.add.group()
      this.scene.time.delayedCall(5000, () => {
        sprite.emoteAnimation()
        this.scene.board?.showRewards(
          group,
          wanderer.data.split(";") as Item[],
          sprite.x,
          sprite.y,
          DEPTH.ITEM_FOUND
        )
      })
      this.scene.time.delayedCall(8000, () => {
        group.destroy(true, true)
      })
    }

    if (wanderer.pkm === Pkm.LAPRAS) {
      this.scene.time.delayedCall(9000, () => {
        sprite.moveManager.setSpeed(350)
        sprite.moveManager.moveTo(15 * 48, -100)
        this.scene.cameras.main.fadeOut(1000, 0, 0, 0)
        this.scene.time.delayedCall(1200, () => {
          this.scene.cameras.main.fadeIn(1000, 0, 0, 0)
          sprite.destroy()
        })
      })
    }
  }

  addWandererPokemonSprite({
    wanderer,
    onClick,
    existingSprite,
    speed = DEFAULT_WANDERER_SPEED
  }: {
    wanderer: Wanderer
    existingSprite?: PokemonSprite
    speed?: number
    onClick?: (
      wanderer: Wanderer,
      pokemon: PokemonSprite,
      pointer: Phaser.Input.Pointer
    ) => boolean
  }): PokemonSprite {
    let caught = false
    const tweens: Phaser.Tweens.Tween[] = []
    let fromLeft = chance(1 / 2)
    if (wanderer.type === WandererType.DIALOG) {
      fromLeft = true // Xatu always comes from left to right to end where the chest is
    }
    if (wanderer.type === WandererType.UNOWN_SPELL) {
      fromLeft = false // Unown spell always comes from right to left to end near Smeargle
    }
    let startX = fromLeft ? -100 : +window.innerWidth + 100,
      startY = 100 + Math.round(Math.random() * 500),
      endX = fromLeft ? +window.innerWidth + 100 : -100,
      endY = 350
    let duration = clamp(window.innerWidth / speed, 4000, 6000)

    if (wanderer.behavior === WandererBehavior.SPECTATE) {
      endX = fromLeft ? 590 : 1512
      endY =
        wanderer.type === WandererType.DIALOG ||
        wanderer.type === WandererType.UNOWN_SPELL
          ? 500
          : 300 + Math.round(Math.random() * 200)
      duration = 4000
    }

    if (wanderer.behavior === WandererBehavior.RUN_THROUGH) {
      endY = 100 + Math.round(Math.random() * 500)
    }

    const sprite =
      existingSprite ??
      new PokemonSprite(
        this.scene,
        startX,
        startY,
        PokemonFactory.createPokemonFromName(wanderer.pkm, {
          shiny: wanderer.shiny
        }),
        "wanderer",
        false,
        false
      )
    sprite.orientation = startX < endX ? Orientation.RIGHT : Orientation.LEFT
    this.scene.animationManager?.animatePokemon(
      sprite,
      PokemonActionState.WALK,
      false
    )

    const tween = this.scene.tweens.add({
      targets: sprite,
      x: endX,
      y: endY,
      ease: "Linear",
      duration,
      onComplete: () => {
        if (wanderer.behavior === WandererBehavior.SPECTATE) {
          this.scene.animationManager?.animatePokemon(
            sprite,
            PokemonActionState.IDLE,
            false
          )
          if (wanderer.type === WandererType.DIALOG) {
            sprite.openDetail()
          } else if (wanderer.type === WandererType.UNOWN_SPELL) {
            sprite.orientation = Orientation.DOWN
            this.scene.animationManager?.animatePokemon(
              sprite,
              PokemonActionState.IDLE,
              false
            )
          }
          tweens.push(
            this.scene.add.tween({
              targets: [sprite],
              ease: "linear",
              duration: 5000,
              delay: 8000,
              x: startX,
              y: startY,
              onComplete: () => {
                sprite.destroy()
              },
              onStart: () => {
                sprite.closeDetail()
                sprite.orientation = Orientation.LEFT
                this.scene.animationManager?.animatePokemon(
                  sprite,
                  PokemonActionState.WALK,
                  false
                )
              }
            })
          )
        } else {
          sprite.destroy()
        }
      }
    })
    tweens.push(tween)

    sprite.draggable = false

    const isReplay = isReplayRoom(this.scene.room)
    if (!isReplay) {
      sprite.sprite.setInteractive()
      sprite.sprite.on("pointerdown", (pointer: Phaser.Input.Pointer) => {
        if (caught || !onClick) return
        caught = onClick(wanderer, sprite, pointer)
        if (caught) tweens.forEach((tween) => tween.destroy())
      })
    }

    return sprite
  }

  displayShardGain(coordinates: number[], index: string, shiny: boolean) {
    const textStyle = {
      fontSize: "25px",
      fontFamily: "Verdana",
      color: shiny ? "#ffd700" : "#fff",
      align: "center",
      strokeThickness: 2,
      stroke: "#000"
    }

    const image = this.scene.add.existing(
      new GameObjects.Image(this.scene, 0, 0, `portrait-${index}`)
        .setScale(0.5, 0.5)
        .setOrigin(0, 0)
    )
    const text = this.scene.add.existing(
      new GameObjects.Text(
        this.scene,
        25,
        0,
        (shiny
          ? SHARDS_PER_SHINY_UNOWN_WANDERER
          : SHARDS_PER_UNOWN_WANDERER
        ).toString(),
        textStyle
      )
    )
    image.setDepth(DEPTH.TEXT_MINOR)
    text.setDepth(DEPTH.TEXT)

    const container = this.scene.add.existing(
      new GameObjects.Container(
        this.scene,
        coordinates[0],
        coordinates[1] - 50,
        [text, image]
      )
    )

    this.scene.add.tween({
      targets: [container],
      ease: "linear",
      duration: 1500,
      delay: 0,
      alpha: {
        getStart: () => 1,
        getEnd: () => 0
      },
      y: {
        getStart: () => coordinates[1] - 50,
        getEnd: () => coordinates[1] - 110
      },
      onComplete: () => {
        container.destroy()
      }
    })
  }
}

function getDialogsBySpecialWanderer(wanderer: Wanderer): {
  dialog?: string
  dialogTitle?: string
} {
  if (wanderer.pkm === Pkm.CHATOT) {
    return {
      dialog: t("npc_dialog.here_are_your_reward", {
        reward: `30 GOLD`
      }),
      dialogTitle: t("npc_dialog.good_job")
    }
  }
  if (wanderer.pkm === Pkm.MAGNEMITE) {
    return {
      dialog: t("npc_dialog.magnemite")
    }
  }
  if (wanderer.pkm === Pkm.MAGNEZONE) {
    return {
      dialog: t("npc_dialog.magnezone")
    }
  }
  if (wanderer.pkm === Pkm.XATU) {
    return {
      dialog: t("npc_dialog.xatu")
    }
  }
  if (wanderer.pkm === Pkm.LAPRAS) {
    return {
      dialog: t("npc_dialog.lapras")
    }
  }
  if (wanderer.pkm === Pkm.KECLEON_PURPLE) {
    return {
      dialog: t("npc_dialog.kecleon_preview"),
      dialogTitle: t("npc_dialog.kecleon")
    }
  }
  return {}
}
